import React, { useEffect, useMemo, useState } from "react";
import { useGetPresignedUrlsMutation } from "../../../app/api/console/consoleImagesApiSlice";
import ContextMeter, { ContextUsageCard } from "./ContextMeter";
import { findFinalLlmCall } from "./contextUsage";
import { OutcomeBadge } from "./OutcomeBadge";
import { formatUsd, formatDuration } from "./outcomeConfig";
import ToolCallDetails, { ToolPayloadPanel } from "./ToolCallDetails";
import TimelineMarkdown from "./TimelineMarkdown";

const SCREENSHOT_TOOLS = new Set(["browsing:screenshot", "evidence:screenshot"]);

export function s3ToHttps(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  if (!u) return null;
  if (u.startsWith("https://") || u.startsWith("http://")) return u;
  if (u.startsWith("s3://")) {
    const rest = u.slice("s3://".length);
    const slash = rest.indexOf("/");
    if (slash <= 0) return null;
    const bucket = rest.slice(0, slash);
    const key = rest.slice(slash + 1);
    if (!bucket || !key) return null;
    return `https://${bucket}.s3.amazonaws.com/${key}`;
  }
  return null;
}

export function toS3Uri(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  if (!u) return null;
  if (u.startsWith("s3://")) return u;
  if (!u.startsWith("http://") && !u.startsWith("https://")) return null;
  try {
    const parsed = new URL(u);
    const host = parsed.hostname;
    const path = parsed.pathname.replace(/^\//, "");
    const vhostMatch = host.match(/^(.+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i);
    if (vhostMatch && path) return `s3://${vhostMatch[1]}/${path}`;
    if (/^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i.test(host) && path.includes("/")) {
      const slash = path.indexOf("/");
      return `s3://${path.slice(0, slash)}/${path.slice(slash + 1)}`;
    }
  } catch {
    return null;
  }
  return null;
}

function looksLikeImageUrl(u) {
  if (!u || typeof u !== "string") return false;
  const s = u.toLowerCase();
  // Journey result JSON / page artifacts live in *-pages buckets — never images.
  if (s.includes("zenyt-staging-pages") || s.includes("zenyt-prod-pages")) return false;
  if (/\.(json|html?)(\?|$)/.test(s)) return false;
  if (s.includes("zenyt-staging-images") || s.includes("zenyt-prod-images")) return true;
  if (s.includes("/screenshots/")) return true;
  return /\.(png|jpe?g|webp|gif)(\?|$)/.test(s);
}

export function isScreenshotTool(name) {
  return !!name && SCREENSHOT_TOOLS.has(String(name));
}

/** Tools whose results intentionally attach evidence images on the timeline. */
const EVIDENCE_SCREENSHOT_TOOLS = new Set([
  "issues:submit_test_result",
  "issues:create_issue",
  "issues:bulk_create_issues",
  "generate_issue",
  "generate_issue_full",
]);

export function showsEvidenceScreenshots(toolName) {
  if (!toolName) return false;
  const name = String(toolName);
  return isScreenshotTool(name) || EVIDENCE_SCREENSHOT_TOOLS.has(name);
}

/** Strip trailing JSON / prose punctuation left on regex URL matches. */
export function scrubExtractedUrlToken(token) {
  if (!token || typeof token !== "string") return "";
  return token.trim().replace(/[),.;:!?'"\]}]+$/g, "");
}

function pushImageUrl(raw, out, seen) {
  const https = s3ToHttps(typeof raw === "string" ? scrubExtractedUrlToken(raw) : raw);
  if (https && looksLikeImageUrl(https) && !seen.has(https)) {
    seen.add(https);
    out.push(https);
  }
}

/** Walk common nested shapes from submit_test_result / issue tools. */
export function collectImageUrlsFromObject(value, out, seen, depth = 0) {
  if (value == null || depth > 6) return;
  if (typeof value === "string") {
    pushImageUrl(value, out, seen);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectImageUrlsFromObject(item, out, seen, depth + 1);
    return;
  }
  if (typeof value !== "object") return;

  for (const k of ["screenshot_url", "url", "image_url", "screenshot", "to"]) {
    if (typeof value[k] === "string") pushImageUrl(value[k], out, seen);
  }
  for (const k of ["screenshot_urls", "images", "screenshots"]) {
    const arr = value[k];
    if (Array.isArray(arr)) {
      for (const item of arr) {
        if (typeof item === "string") pushImageUrl(item, out, seen);
        else collectImageUrlsFromObject(item, out, seen, depth + 1);
      }
    }
  }
  // Nested envelopes: { result: {...} }, journey_gate_screenshots, item_results, resolutions
  for (const k of [
    "result",
    "details",
    "item_results",
    "journey_gate_screenshots",
    "shot_catalog",
    "resolutions",
    "journey_gate_auto_screenshot",
  ]) {
    if (value[k] != null) collectImageUrlsFromObject(value[k], out, seen, depth + 1);
  }
}

export function extractImageUrlsFromResult(resultText) {
  const out = [];
  const seen = new Set();
  const text = String(resultText || "");
  const trimmed = text.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const p = JSON.parse(trimmed);
      collectImageUrlsFromObject(p, out, seen);
    } catch {
      /* fall through to regex */
    }
  }
  // Regex fallback — stop at JSON/prose delimiters so we never glue two URLs.
  const m = trimmed.match(/(https?:\/\/[^\s"'<>\\]+|s3:\/\/[^\s"'<>\\]+)/g);
  if (m) m.forEach((token) => pushImageUrl(token, out, seen));
  return out;
}

export function collectScreenshotUrls({ resultText, details, screenshotUrls, args } = {}) {
  const out = [];
  const seen = new Set();
  if (Array.isArray(screenshotUrls)) {
    screenshotUrls.forEach((u) => pushImageUrl(u, out, seen));
  }
  if (resultText) {
    extractImageUrlsFromResult(resultText).forEach((url) => {
      if (!seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    });
  }
  if (details && typeof details === "object") {
    collectImageUrlsFromObject(details, out, seen);
  }
  // submit_test_result args may still cite shot-N aliases; only real URLs count.
  if (args && typeof args === "object") {
    collectImageUrlsFromObject(args, out, seen);
  }
  return out;
}

function extractToolResultFields(message) {
  if (!message || typeof message !== "object" || message.role !== "tool_result") return null;
  const texts = [];
  const images = [];
  for (const block of message.content || []) {
    if (!block || typeof block !== "object") continue;
    if (block.type === "text" && block.text) texts.push(String(block.text));
    if (block.type === "image" && block.source) {
      const src = block.source;
      if (typeof src === "string") images.push(src);
      else if (src.url) images.push(src.url);
    }
  }
  const resultText = texts.join("\n");
  const toolName = message.tool_name || null;
  const screenshotUrls = showsEvidenceScreenshots(toolName)
    ? collectScreenshotUrls({ resultText, details: message.details, screenshotUrls: images })
    : [];
  return {
    role: "tool_result",
    tool_name: toolName,
    tool_call_id: message.tool_call_id || null,
    result_text: resultText || null,
    details: message.details,
    screenshot_urls: screenshotUrls,
    is_error: !!message.is_error,
  };
}

export function trimText(value, limit) {
  if (value == null) return null;
  const text = typeof value === "string" ? value : JSON.stringify(value);
  if (text.length <= limit) return typeof value === "string" ? value : text;
  return `${text.slice(0, limit)}…`;
}

function parseMessageBlocks(content) {
  if (content == null) return { texts: [], toolUses: [], images: [] };
  if (typeof content === "string") return { texts: [content], toolUses: [], images: [] };
  if (!Array.isArray(content)) {
    return { texts: [trimText(content, 800)], toolUses: [], images: [] };
  }
  const texts = [];
  const toolUses = [];
  const images = [];
  for (const block of content) {
    if (!block || typeof block !== "object") continue;
    const kind = block.type;
    if (kind === "text" && block.text) texts.push(String(block.text));
    else if (kind === "tool_use" || kind === "tool_call") {
      toolUses.push({
        id: block.id,
        name: block.name || block.tool_name,
        input: block.input || block.args,
      });
    } else if (kind === "image" && block.source) {
      const src = block.source;
      if (typeof src === "string") images.push(src);
      else if (src.url) images.push(src.url);
    } else if (block.text) {
      texts.push(String(block.text));
    }
  }
  return { texts, toolUses, images };
}

function extractFieldsFromParsed(p) {
  const t = p.type || p.event;
  const f = {};
  if (t === "tool_call") {
    f.tool_call_id = p.tool_call_id;
    f.tool_name = p.tool_name;
    f.duration_ms = p.duration_ms;
    f.blocked_reason = p.blocked_reason;
    f.args = p.args;
    f.result_text = p.result_text;
    if (showsEvidenceScreenshots(p.tool_name)) {
      f.screenshot_urls = collectScreenshotUrls({
        resultText: p.result_text,
        details: p.details,
        args: p.args,
      });
    }
  } else if (t === "llm_call") {
    f.model = p.model;
    f.duration_ms = p.duration_ms;
    f.cost_usd = p.cost_usd;
    f.usage = p.usage;
    f.stop_reason = p.stop_reason;
    f.context = p.context;
  } else if (t === "subagent_spawn" || t === "subagent_completion") {
    f.child_agent_id = p.child_agent_id;
    f.child_run_id = p.child_run_id;
    f.task = p.task; // keep full structured task (JSON object or long string)
    f.status = p.status;
    f.duration_ms = p.duration_ms;
  } else if (t === "user_reply" || t === "thinking") {
    f.text = typeof p.text === "string" ? p.text : trimText(p.text, 50000);
  } else if (t === "hook_notice") {
    f.hook = p.hook;
    f.severity = p.severity;
    f.text = trimText(p.text, 800);
  } else if (t === "turn_start" || t === "turn_end") {
    f.turn_index = p.turn_index;
  } else if (t === "agent_start") {
    f.session_id = p.session_id;
    f.website_id = p.website_id;
    f.channel = p.channel;
  } else if (t === "agent_end") {
    f.stop_reason = p.stop_reason;
    f.error_message = trimText(p.error_message, 800);
  } else if (t === "message") {
    const msg = p.message;
    if (msg && typeof msg === "object") {
      if (msg.role === "tool_result") {
        return extractToolResultFields(msg) || {};
      }
      f.role = msg.role;
      f.blocks = parseMessageBlocks(msg.content);
    } else if (msg != null) {
      f.blocks = { texts: [typeof msg === "string" ? msg : trimText(msg, 50000)], toolUses: [], images: [] };
    }
  }
  return f;
}

export function getEventFields(ev) {
  let fields = {};
  if (ev.fields && Object.keys(ev.fields).length > 0) {
    fields = { ...ev.fields };
  } else if (ev.data) {
    try {
      const parsed = JSON.parse(ev.data);
      if (parsed && typeof parsed === "object") fields = extractFieldsFromParsed(parsed);
    } catch {
      fields = { text: ev.data };
    }
  }

  // Ensure measured context from llm_call payloads survives even when the
  // backend pre-extracted a partial `fields` object without `context`.
  if (ev.type === "llm_call" && fields.context == null && ev.data) {
    try {
      const parsed = JSON.parse(ev.data);
      if (parsed?.context) fields.context = parsed.context;
    } catch {
      /* ignore */
    }
  }

  if (ev.type === "message") {
    if (fields.role === "tool_result") {
      if (showsEvidenceScreenshots(fields.tool_name) && (!fields.screenshot_urls || !fields.screenshot_urls.length)) {
        try {
          const parsed = JSON.parse(ev.data);
          const msg = parsed?.message;
          if (msg && typeof msg === "object") {
            const extracted = extractToolResultFields(msg);
            if (extracted?.screenshot_urls?.length) {
              fields.screenshot_urls = extracted.screenshot_urls;
            }
          }
        } catch {
          /* ignore */
        }
      }
      return fields;
    }
    if (fields.blocks) return fields;
    const content = fields.text ?? fields.content;
    if (content != null) {
      let blocks;
      if (typeof content === "string" && content.trim().startsWith("[")) {
        try {
          blocks = parseMessageBlocks(JSON.parse(content));
        } catch {
          blocks = parseMessageBlocks(content);
        }
      } else {
        blocks = parseMessageBlocks(content);
      }
      return { ...fields, blocks };
    }
    if (ev.data && !fields.role) {
      try {
        const parsed = JSON.parse(ev.data);
        const toolResult = extractToolResultFields(parsed?.message);
        if (toolResult) return toolResult;
      } catch {
        /* ignore */
      }
    }
  }

  if (ev.type === "tool_call" && ev.data) {
    try {
      const parsed = JSON.parse(ev.data);
      if (parsed?.tool_call_id && !fields.tool_call_id) fields.tool_call_id = parsed.tool_call_id;
      // Prefer full structured args/result from the raw event when the compact
      // fields payload was stringified or truncated mid-JSON (legacy backend).
      fields.args = preferRicherToolPayload(fields.args, parsed?.args);
      fields.result_text = preferRicherToolPayload(fields.result_text, parsed?.result_text);
      if (showsEvidenceScreenshots(fields.tool_name || parsed?.tool_name) && !fields.screenshot_urls?.length) {
        fields.screenshot_urls = collectScreenshotUrls({
          resultText: fields.result_text || parsed?.result_text,
          details: parsed?.details || fields.details,
          args: fields.args || parsed?.args,
        });
      }
    } catch {
      /* ignore */
    }
  }

  if ((ev.type === "subagent_spawn" || ev.type === "subagent_completion") && ev.data) {
    try {
      const parsed = JSON.parse(ev.data);
      fields.task = preferRicherToolPayload(fields.task, parsed?.task);
      if (parsed?.child_run_id && !fields.child_run_id) fields.child_run_id = parsed.child_run_id;
      if (parsed?.child_agent_id && !fields.child_agent_id) fields.child_agent_id = parsed.child_agent_id;
    } catch {
      /* ignore */
    }
  }

  return fields;
}

function payloadRichness(value) {
  if (value == null) return 0;
  if (typeof value === "object") {
    try {
      return JSON.stringify(value).length + 1000; // prefer structured
    } catch {
      return 1000;
    }
  }
  const text = String(value);
  // Heavily penalize truncated compact JSON from the old 1k _trim path.
  if (/[…\u2026]/.test(text)) return Math.max(text.length - 5000, 1);
  return text.length;
}

function preferRicherToolPayload(current, fallback) {
  if (fallback == null) return current;
  if (current == null) return fallback;
  return payloadRichness(fallback) > payloadRichness(current) ? fallback : current;
}

export function formatUsage(usage) {
  if (!usage) return null;
  const inp = usage.input_tokens ?? usage.prompt_tokens;
  const out = usage.output_tokens ?? usage.completion_tokens;
  if (inp == null && out == null) return null;
  return `${inp ?? "?"} in / ${out ?? "?"} out`;
}

export function formatMsDuration(ms) {
  if (ms == null) return null;
  return formatDuration(ms / 1000);
}

export function formatToolArgs(toolName, args) {
  let resolved = args;
  if (typeof args === "string") {
    try {
      const trimmed = args.trim().replace(/[…\u2026]+\s*$/, "");
      resolved = JSON.parse(trimmed.startsWith("{") || trimmed.startsWith("[") ? trimmed : args);
    } catch {
      return trimText(args, 120);
    }
  }
  if (!resolved || typeof resolved !== "object") return null;
  if (toolName === "browsing:navigate" && resolved.url) return resolved.url;
  if (toolName === "browsing:click" && resolved.selector) return resolved.selector;
  if (toolName === "browsing:fill" && resolved.selector) {
    return `${resolved.selector}${resolved.value != null ? ` → "${String(resolved.value).slice(0, 40)}"` : ""}`;
  }
  if (toolName === "browsing:screenshot") {
    const parts = [];
    if (resolved.full_page) parts.push("full page");
    if (resolved.viewport) parts.push(resolved.viewport);
    return parts.length ? parts.join(" · ") : null;
  }
  const keys = Object.keys(resolved);
  if (keys.length === 1) return trimText(resolved[keys[0]], 120);
  if (keys.length <= 3) {
    return keys.map((k) => `${k}: ${trimText(resolved[k], 60)}`).join(" · ");
  }
  return null;
}

function normalizeImageUri(url) {
  return toS3Uri(url) || (typeof url === "string" ? url.trim() : null);
}

export function ScreenshotGallery({ urls, caption }) {
  const [getPresignedUrls] = useGetPresignedUrlsMutation();
  const [displayUrls, setDisplayUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [zoomLevel, setZoomLevel] = useState("fit"); // "fit" or "original"

  const sourceKey = useMemo(
    () => (urls || []).map((u) => normalizeImageUri(u)).filter(Boolean).join("\0"),
    [urls]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const normalized = (urls || []).map((u) => u?.trim()).filter(Boolean);
      if (!normalized.length) {
        setDisplayUrls([]);
        setLoading(false);
        setFailed(false);
        return;
      }

      setLoading(true);
      setFailed(false);
      setDisplayUrls([]);

      try {
        // Collect all S3 URIs to presign in a single batched request
        const s3Uris = [];
        const uriMapping = []; // Tracks index to map back resolved URLs

        normalized.forEach((uri, index) => {
          const s3Uri = uri.startsWith("s3://") ? uri : toS3Uri(uri);
          if (s3Uri) {
            s3Uris.push(s3Uri);
            uriMapping.push({ type: "s3", index, original: uri });
          } else {
            uriMapping.push({ type: "http", index, original: uri });
          }
        });

        let presignedUrls = [];
        if (s3Uris.length > 0) {
          const res = await getPresignedUrls(s3Uris).unwrap();
          presignedUrls = res.presigned_urls || [];
        }

        const resolved = new Array(normalized.length);
        let s3Count = 0;

        uriMapping.forEach((item) => {
          if (item.type === "s3") {
            resolved[item.index] = presignedUrls[s3Count] || item.original;
            s3Count++;
          } else {
            resolved[item.index] = item.original;
          }
        });

        if (!cancelled) {
          setDisplayUrls(resolved.filter(Boolean));
          setFailed(resolved.filter(Boolean).length === 0);
        }
      } catch (err) {
        console.error("Failed to load screenshots:", err);
        if (!cancelled) {
          setFailed(true);
          setDisplayUrls([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [sourceKey, getPresignedUrls, urls]);

  // Keyboard navigation for Lightbox
  useEffect(() => {
    if (activeImageIndex === null) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveImageIndex(null);
      } else if (e.key === "ArrowRight") {
        setActiveImageIndex((prev) => (prev + 1) % displayUrls.length);
      } else if (e.key === "ArrowLeft") {
        setActiveImageIndex((prev) => (prev - 1 + displayUrls.length) % displayUrls.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeImageIndex, displayUrls]);

  if (!urls?.length) return null;

  const handleThumbnailClick = (e, index) => {
    e.preventDefault();
    setActiveImageIndex(index);
    setZoomLevel("fit");
  };

  return (
    <div className='mt-2'>
      {caption && <div className='text-[10px] font-semibold uppercase text-gray-400 mb-1'>{caption}</div>}
      {loading && (
        <div className='text-xs text-gray-400 py-2 flex items-center gap-2'>
          <svg className="animate-spin h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading screenshot…
        </div>
      )}
      {failed && !loading && (
        <div className='text-xs text-red-600 mb-1'>Could not load screenshot preview.</div>
      )}
      {!loading && !displayUrls.length && urls?.length > 0 && (
        <div className='text-xs text-gray-400 mb-1'>No preview available.</div>
      )}
      <div className='flex flex-wrap gap-2'>
        {displayUrls.map((url, i) => (
          <button
            key={`${url}-${i}`}
            type='button'
            onClick={(e) => handleThumbnailClick(e, i)}
            className='block rounded-lg border border-gray-200 overflow-hidden hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm group bg-gray-50'
            title='Click to expand screenshot'
          >
            <div className="relative overflow-hidden">
              <img
                src={url}
                alt={`Screenshot ${i + 1}`}
                loading='lazy'
                className='max-h-64 max-w-md w-auto object-contain bg-gray-50 transition-transform duration-200 group-hover:scale-[1.02]'
                onError={(e) => {
                  e.currentTarget.classList.add("opacity-40");
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="bg-white/90 text-gray-800 text-xs font-semibold px-2.5 py-1 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                  Expand
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && (
        <div 
          className='fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 select-none'
          onClick={() => setActiveImageIndex(null)}
        >
          {/* Header Controls */}
          <div 
            className="absolute top-4 left-4 right-4 flex items-center justify-between text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-black/40 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide border border-white/10">
              {caption || "Screenshot"} ({activeImageIndex + 1} of {displayUrls.length})
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setZoomLevel(zoomLevel === "fit" ? "original" : "fit")}
                className="bg-white/10 hover:bg-white/20 active:bg-white/30 backdrop-blur border border-white/10 text-white rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all flex items-center gap-1.5"
                title={zoomLevel === "fit" ? "View actual size" : "Fit to screen"}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {zoomLevel === "fit" ? "Actual Size (100%)" : "Fit Screen"}
              </button>
              <a
                href={displayUrls[activeImageIndex]}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 active:bg-white/30 backdrop-blur border border-white/10 text-white rounded-full p-2 transition-all"
                title="Open original image in new tab"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <button
                type="button"
                onClick={() => setActiveImageIndex(null)}
                className="bg-white/10 hover:bg-white/20 active:bg-white/30 backdrop-blur border border-white/10 text-white rounded-full p-2 transition-all"
                title="Close (Esc)"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Left Arrow */}
          {displayUrls.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) => (prev - 1 + displayUrls.length) % displayUrls.length);
              }}
              className="absolute left-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 border border-white/10 transition-all hover:scale-105 active:scale-95"
              title="Previous (ArrowLeft)"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Main Image Container */}
          <div 
            className={`w-full h-full flex items-center justify-center p-12 overflow-auto ${
              zoomLevel === "original" ? "cursor-zoom-out" : "cursor-zoom-in"
            }`}
            onClick={() => setZoomLevel(zoomLevel === "fit" ? "original" : "fit")}
          >
            <img
              src={displayUrls[activeImageIndex]}
              alt={`Screenshot ${activeImageIndex + 1}`}
              className={`transition-all duration-150 select-none bg-gray-900 shadow-2xl rounded-lg ${
                zoomLevel === "fit" 
                  ? "max-h-full max-w-full object-contain" 
                  : "max-h-none max-w-none w-auto h-auto object-none"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setZoomLevel(zoomLevel === "fit" ? "original" : "fit");
              }}
            />
          </div>

          {/* Right Arrow */}
          {displayUrls.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) => (prev + 1) % displayUrls.length);
              }}
              className="absolute right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 border border-white/10 transition-all hover:scale-105 active:scale-95"
              title="Next (ArrowRight)"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function prepareCleanEvents(events, showSystemEvents) {
  const processed = [];
  let lastLlmCall = null;

  const toolResultsByCallId = new Map();
  for (const ev of events) {
    if (ev.type === "message") {
      const fields = getEventFields(ev);
      if (fields.role === "tool_result" && fields.tool_call_id) {
        toolResultsByCallId.set(fields.tool_call_id, fields);
      }
    }
  }

  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    const type = ev.type || "event";
    const fields = getEventFields(ev);

    if (type === "llm_call") {
      lastLlmCall = fields;
      if (!showSystemEvents) {
        continue;
      }
    }

    if (
      (type === "turn_start" ||
        type === "turn_end" ||
        type === "agent_start" ||
        type === "agent_end") &&
      !showSystemEvents
    ) {
      continue;
    }

    if (type === "message" && fields.role === "tool_result") {
      if (fields.tool_call_id && !showSystemEvents) {
        continue;
      }
    }

    const enriched = { ...ev, _fields: fields };

    if (lastLlmCall && (type === "message" || type === "tool_call")) {
      enriched.llmMeta = lastLlmCall;
      lastLlmCall = null;
    }

    if (type === "tool_call" && fields.tool_call_id) {
      enriched.pairedResult = toolResultsByCallId.get(fields.tool_call_id) || null;
    }

    processed.push(enriched);
  }

  return processed;
}

function getEventTimelineConfig(type, isError, fields) {
  if (isError) {
    return {
      label: "Error",
      badgeClass: "bg-red-50 text-red-700 border border-red-100",
      iconBorder: "border-red-200",
      iconBg: "bg-red-50",
      iconText: "text-red-600",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    };
  }

  switch (type) {
    case "thinking":
      return {
        label: "Thinking",
        badgeClass: "bg-amber-50 text-amber-800 border border-amber-100",
        iconBorder: "border-amber-200",
        iconBg: "bg-amber-50",
        iconText: "text-amber-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        ),
      };
    case "tool_call":
      return {
        label: "Tool Call",
        badgeClass: "bg-blue-50 text-blue-800 border border-blue-100",
        iconBorder: "border-blue-200",
        iconBg: "bg-blue-50",
        iconText: "text-blue-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      };
    case "message":
      const isAssistant = fields.role === "assistant";
      return {
        label: isAssistant ? "Assistant" : "User",
        badgeClass: isAssistant
          ? "bg-purple-50 text-purple-800 border border-purple-100"
          : "bg-emerald-50 text-emerald-800 border border-emerald-100",
        iconBorder: isAssistant ? "border-purple-200" : "border-emerald-200",
        iconBg: isAssistant ? "bg-purple-50" : "bg-emerald-50",
        iconText: isAssistant ? "text-purple-600" : "text-emerald-600",
        icon: isAssistant ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      };
    case "user_reply":
      return {
        label: "User Reply",
        badgeClass: "bg-emerald-50 text-emerald-800 border border-emerald-100",
        iconBorder: "border-emerald-200",
        iconBg: "bg-emerald-50",
        iconText: "text-emerald-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      };
    case "subagent_spawn":
    case "subagent_completion":
      return {
        label: type === "subagent_spawn" ? "Subagent Spawn" : "Subagent Done",
        badgeClass: "bg-indigo-50 text-indigo-800 border border-indigo-100",
        iconBorder: "border-indigo-200",
        iconBg: "bg-indigo-50",
        iconText: "text-indigo-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a3 3 0 003-3V12a6 6 0 00-3-5.197m0 11.917A3 3 0 1018 12.72v3m0 3V12m0-3a3 3 0 10-3-3v3m0 3h-3m0 0a3 3 0 10-3 3v-3m0 0a3 3 0 103-3h3m-3 3H6a3 3 0 01-3-3V9a6 6 0 013-5.197" />
          </svg>
        ),
      };
    case "hook_notice":
      return {
        label: "System Notice",
        badgeClass: "bg-slate-50 text-slate-800 border border-slate-100",
        iconBorder: "border-slate-200",
        iconBg: "bg-slate-50",
        iconText: "text-slate-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      };
    case "llm_call":
      return {
        label: "LLM Generation",
        badgeClass: "bg-violet-50 text-violet-800 border border-violet-100",
        iconBorder: "border-violet-200",
        iconBg: "bg-violet-50",
        iconText: "text-violet-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        ),
      };
    case "agent_start":
      return {
        label: "Start",
        badgeClass: "bg-gray-50 text-gray-800 border border-gray-100",
        iconBorder: "border-gray-200",
        iconBg: "bg-gray-50",
        iconText: "text-gray-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      };
    case "agent_end":
      return {
        label: "End",
        badgeClass: "bg-gray-50 text-gray-800 border border-gray-100",
        iconBorder: "border-gray-200",
        iconBg: "bg-gray-50",
        iconText: "text-gray-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      };
    default:
      return {
        label: type,
        badgeClass: "bg-gray-50 text-gray-800 border border-gray-100",
        iconBorder: "border-gray-200",
        iconBg: "bg-gray-50",
        iconText: "text-gray-600",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      };
  }
}

function CollapsiblePre({ label, value }) {
  if (value == null || value === "") return null;
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  const long = text.length > 200;
  if (!long) {
    return (
      <div className="mt-1">
        <ToolPayloadPanel label={label} value={value} />
      </div>
    );
  }
  return (
    <details className="mt-1 group">
      <summary className="text-[10px] font-semibold uppercase text-gray-400 cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 group-open:rotate-90 transition-transform">
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        {label} ({text.length} chars)
      </summary>
      <div className="mt-2">
        <ToolPayloadPanel label={label} value={value} showHeader={false} />
      </div>
    </details>
  );
}

function MessageBody({ fields }) {
  const blocks = fields.blocks || { texts: [], toolUses: [], images: [] };
  const fullText = blocks.texts.join("\n\n");

  return (
    <div className="space-y-2">
      <TimelineMarkdown text={fullText} />
      {blocks.toolUses.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100/50">
          <div className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider mb-1">
            Decided to call tools:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {blocks.toolUses.map((tu, i) => (
              <div
                key={`tu-${i}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-100/60 text-xs text-blue-800 font-mono"
              >
                <span className="font-semibold">{tu.name || "tool"}</span>
                {formatToolArgs(tu.name, tu.input) && (
                  <span
                    className="text-blue-600/80 text-[11px] truncate max-w-[180px]"
                    title={formatToolArgs(tu.name, tu.input)}
                  >
                    ({formatToolArgs(tu.name, tu.input)})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <ScreenshotGallery urls={blocks.images.map(s3ToHttps).filter(Boolean)} />
    </div>
  );
}

function CleanEventBody({ type, fields, isError, pairedResult, events, eventIndex }) {
  const [expanded, setExpanded] = useState(false);
  const [thinkingExpanded, setThinkingExpanded] = useState(false);

  if (type === "thinking") {
    const text = fields.text || "(empty)";
    const isLong = text.length > 250;

    return (
      <div className='bg-amber-50/40 border border-amber-100/70 rounded-2xl p-4 text-gray-700 italic text-sm shadow-sm relative w-full max-w-none'>
        <div className="absolute -top-2.5 left-4 bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-200/50">
          Thought Process
        </div>
        <p className="leading-relaxed">
          {isLong && !thinkingExpanded ? `${text.slice(0, 250)}...` : text}
        </p>
        {isLong && (
          <button
            type="button"
            onClick={() => setThinkingExpanded(!thinkingExpanded)}
            className="mt-2 text-xs font-semibold text-amber-700 hover:text-amber-900 underline focus:outline-none flex items-center gap-1"
          >
            {thinkingExpanded ? "Show less" : "Read full thinking process..."}
          </button>
        )}
      </div>
    );
  }

  if (type === "tool_call") {
    const argPreview = formatToolArgs(fields.tool_name, fields.args);
    const toolName = fields.tool_name || "";
    // Only attach galleries to capture tools and tools that file evidence —
    // avoid dumping S3 URLs mined from unrelated tool payloads onto the timeline.
    const showShots = showsEvidenceScreenshots(toolName);
    const uniqueShotUrls = showShots
      ? [
          ...new Set(
            collectScreenshotUrls({
              resultText: fields.result_text || pairedResult?.result_text,
              details: fields.details || pairedResult?.details,
              args: fields.args,
              screenshotUrls: [
                ...(fields.screenshot_urls || []),
                ...(pairedResult?.screenshot_urls || []),
              ],
            })
          ),
        ]
      : [];
    const shotCaption = isScreenshotTool(toolName)
      ? "Captured Screenshots"
      : "Evidence Screenshots";

    return (
      <div className="flex flex-col gap-2 w-full max-w-none">
        <div className="flex flex-wrap items-center gap-2 bg-gray-50/50 border border-gray-100 rounded-xl px-3 py-2 text-sm">
          <span className="font-mono font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
            {fields.tool_name || "tool"}
          </span>
          {argPreview && (
            <span className="text-xs text-gray-600 truncate max-w-md font-medium" title={argPreview}>
              {argPreview}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {fields.duration_ms != null && (
              <span className="text-xs text-gray-400 font-mono">{formatMsDuration(fields.duration_ms)}</span>
            )}
            {uniqueShotUrls.length > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold border border-emerald-200">
                {uniqueShotUrls.length} IMG
              </span>
            )}
            {isError && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-bold border border-red-200">
                FAIL
              </span>
            )}
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 underline focus:outline-none ml-2"
            >
              {expanded ? "Hide Details" : "Show Details"}
            </button>
          </div>
        </div>

        {/* Captures stay on browsing:screenshot; evidence for submit_test_result /
            create_issue sits under that same tool chip so the timeline reads as
            "this report filed with these proofs". */}
        {uniqueShotUrls.length > 0 && (
          <ScreenshotGallery urls={uniqueShotUrls} caption={shotCaption} />
        )}

        {expanded && (
          <ToolCallDetails
            args={fields.args}
            result={pairedResult?.result_text || fields.result_text}
            isError={isError}
          />
        )}
      </div>
    );
  }

  if (type === "llm_call") {
    const usage = formatUsage(fields.usage);
    return (
      <div className="space-y-2 w-full max-w-2xl">
        <div className='flex flex-wrap items-center gap-2 text-xs text-gray-500 bg-violet-50/20 border border-violet-100/50 rounded-xl px-3 py-1.5 font-mono'>
          <span className='font-bold text-gray-700'>{fields.model || "llm"}</span>
          {usage && <span>· {usage}</span>}
          {fields.cost_usd != null && (
            <span className='font-semibold text-violet-700'>· {formatUsd(fields.cost_usd)}</span>
          )}
          {fields.duration_ms != null && (
            <span>· {formatMsDuration(fields.duration_ms)}</span>
          )}
          {fields.stop_reason && (
            <span className='ml-auto text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-sans font-semibold uppercase'>
              {fields.stop_reason}
            </span>
          )}
        </div>
        <ContextUsageCard
          usage={fields.usage}
          llmMeta={fields}
          events={events}
          eventIndex={eventIndex}
        />
      </div>
    );
  }

  if (type === "message") {
    if (fields.role === "tool_result") {
      const shotUrls = collectScreenshotUrls({
        resultText: fields.result_text,
        details: fields.details,
        screenshotUrls: fields.screenshot_urls,
      });
      if (isScreenshotTool(fields.tool_name) && shotUrls.length) {
        return (
          <div className="w-full max-w-none">
            <div className='flex flex-wrap items-center gap-2 mb-1'>
              <span className='font-mono text-sm text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200'>{fields.tool_name}</span>
              <span className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold border border-emerald-200'>
                screenshot result
              </span>
            </div>
            <ScreenshotGallery urls={shotUrls} caption='Screenshot' />
          </div>
        );
      }
      return (
        <div className="w-full max-w-none">
          <div className='flex flex-wrap items-center gap-2 mb-1'>
            <span className='font-mono text-sm text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200'>{fields.tool_name || "tool"}</span>
            <span className='text-[10px] text-gray-400 font-medium'>result</span>
          </div>
          <CollapsiblePre label='Result' value={fields.result_text} />
        </div>
      );
    }

    const isAssistant = fields.role === "assistant";
    const bubbleBg = isAssistant ? "bg-purple-50/20 border-purple-100/60" : "bg-emerald-50/20 border-emerald-100/60";
    const titleColor = isAssistant ? "text-purple-800" : "text-emerald-800";
    const titleLabel = isAssistant ? "Assistant Response" : "User Message";

    return (
      <div className={`border rounded-2xl p-4 shadow-sm relative w-full max-w-none ${bubbleBg}`}>
        <div className={`absolute -top-2.5 left-4 bg-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${titleColor}`}>
          {titleLabel}
        </div>
        <MessageBody fields={fields} />
      </div>
    );
  }

  if (type === "subagent_spawn" || type === "subagent_completion") {
    const isSpawn = type === "subagent_spawn";
    const cardBg = isSpawn ? "bg-indigo-50/10 border-indigo-100/50" : "bg-indigo-50/20 border-indigo-100/70";
    return (
      <div className={`border rounded-xl p-3 w-full max-w-none shadow-sm ${cardBg}`}>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${isSpawn ? "bg-indigo-100 text-indigo-800" : "bg-emerald-100 text-emerald-800"}`}>
            {isSpawn ? "Spawn Subagent" : "Subagent Finished"}
          </span>
          <span className="font-mono text-sm font-semibold text-gray-900">{fields.child_agent_id || "?"}</span>
          {fields.duration_ms != null && (
            <span className="text-xs text-gray-400 font-mono ml-auto">{formatMsDuration(fields.duration_ms)}</span>
          )}
        </div>
        {fields.child_run_id && (
          <div className="text-[10px] text-gray-400 font-mono mt-1">Run ID: {fields.child_run_id}</div>
        )}
        {fields.task != null && fields.task !== "" && (
          <div className="mt-2">
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">
              Task Description
            </div>
            <TimelineMarkdown text={fields.task} label="Task" />
          </div>
        )}
        {fields.status && (
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            <span className="text-gray-500">Completion Status:</span>
            <span className={`font-semibold ${fields.status === "ok" ? "text-emerald-600" : "text-red-600"}`}>
              {fields.status.toUpperCase()}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (type === "user_reply") {
    return (
      <div className="border border-emerald-100/60 bg-emerald-50/20 rounded-2xl p-4 shadow-sm relative w-full max-w-none">
        <div className="absolute -top-2.5 left-4 bg-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-emerald-800 border-emerald-100/60">
          User Reply
        </div>
        <TimelineMarkdown text={fields.text || ""} />
      </div>
    );
  }

  if (type === "hook_notice") {
    const isErr = fields.severity === "error";
    const isWrn = fields.severity === "warn";
    const bannerBg = isErr ? "bg-red-50/30 border-red-100" : isWrn ? "bg-amber-50/30 border-amber-100" : "bg-blue-50/30 border-blue-100";
    const textColor = isErr ? "text-red-800" : isWrn ? "text-amber-800" : "text-blue-800";
    const badgeColor = isErr ? "bg-red-100 text-red-800" : isWrn ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800";

    return (
      <div className={`border rounded-xl p-3 w-full max-w-none flex items-start gap-2.5 shadow-sm ${bannerBg}`}>
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider shrink-0 mt-0.5 ${badgeColor}`}>
          {fields.severity || "info"}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] font-semibold text-gray-700">{fields.hook}</div>
          {fields.text && <div className={`text-xs mt-1 leading-relaxed ${textColor}`}>{fields.text}</div>}
        </div>
      </div>
    );
  }

  if (type === "turn_start" || type === "turn_end") {
    return (
      <div className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
        {type === "turn_start" ? "Turn start" : "Turn end"} · turn {fields.turn_index ?? "?"}
      </div>
    );
  }

  if (type === "agent_start") {
    return (
      <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200/60 rounded-full px-3 py-1 text-xs text-gray-600 font-medium">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span>Agent session started</span>
        {fields.channel && <span className="text-[10px] text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded-full">channel: {fields.channel}</span>}
      </div>
    );
  }

  if (type === "agent_end") {
    const hasError = !!fields.error_message;
    return (
      <div className={`inline-flex flex-col gap-1 border rounded-xl p-3 w-full max-w-2xl ${hasError ? "bg-red-50/30 border-red-100 text-red-800" : "bg-gray-50 border-gray-200 text-gray-700"}`}>
        <div className="flex items-center gap-2 text-xs font-semibold">
          <span className={`w-2 h-2 rounded-full ${hasError ? "bg-red-500" : "bg-gray-400"}`} />
          <span>Agent session ended</span>
          {fields.stop_reason && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">
              {fields.stop_reason}
            </span>
          )}
        </div>
        {fields.error_message && (
          <div className="text-xs text-red-700 font-mono whitespace-pre-wrap mt-1 bg-white/50 p-2 rounded border border-red-100/50">
            {fields.error_message}
          </div>
        )}
      </div>
    );
  }

  return (
    <pre className='text-xs text-gray-600 whitespace-pre-wrap break-all bg-gray-50 rounded-lg p-3 border border-gray-100 font-mono'>
      {fields.text || "(no preview)"}
    </pre>
  );
}

function CleanEventRow({ ev, nested, events, index }) {
  const type = ev.type || "event";
  const fields = ev._fields || getEventFields(ev);
  const pairedResult = ev.pairedResult || null;
  const llmMeta = ev.llmMeta || null;

  const config = getEventTimelineConfig(type, ev.is_error, fields);

  const ts = new Date(ev.timestamp);
  const absTime = Number.isNaN(ts.getTime()) ? "—" : ts.toLocaleString();
  const timeStr = Number.isNaN(ts.getTime())
    ? "—"
    : ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div className='relative pl-8 group'>
      {/* Timeline Dot/Icon */}
      <div
        className={`absolute -left-[17px] top-0.5 w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center shadow-sm transition-all group-hover:scale-105 ${config.iconBorder} ${config.iconBg} ${config.iconText}`}
        title={`${type} at ${absTime}`}
      >
        {config.icon}
      </div>

      {/* Main Content Area */}
      <div className='flex flex-col gap-1'>
        {/* Header line */}
        <div className='flex items-center justify-between gap-4 flex-wrap'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${config.badgeClass}`}>
              {config.label}
            </span>
            <span className='text-[11px] text-gray-400 font-mono'>{timeStr}</span>
            {llmMeta && (
              <span className='text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-100 font-medium flex items-center gap-1'>
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {llmMeta.model} · {formatUsd(llmMeta.cost_usd)} · {formatMsDuration(llmMeta.duration_ms)}
              </span>
            )}
            {llmMeta?.usage && (
              <ContextMeter
                llmMeta={llmMeta}
                usage={llmMeta.usage}
                events={events}
                eventIndex={index}
              />
            )}
          </div>
        </div>

        {/* Body content */}
        <div className='mt-1 text-sm text-gray-800'>
          <CleanEventBody
            type={type}
            fields={fields}
            isError={ev.is_error}
            pairedResult={pairedResult}
            events={events}
            eventIndex={index}
          />
        </div>

        {/* Raw JSON Toggle */}
        {ev.data && (
          <details className='mt-2 group/raw'>
            <summary className='text-[10px] text-gray-400 cursor-pointer hover:text-gray-600 select-none inline-flex items-center gap-1'>
              <span>Raw JSON</span>
              <span className='opacity-0 group-hover/raw:opacity-100 transition-opacity'>(click to expand)</span>
            </summary>
            <pre className='text-[11px] text-gray-500 whitespace-pre-wrap break-all mt-1.5 max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-100 font-mono'>
              {ev.data}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

const RUN_KIND = {
  orchestrator: {
    label: "Orchestrator subchat",
    short: "Orchestrator",
    border: "border-l-indigo-500",
    header: "bg-indigo-50 border-indigo-100",
    pill: "bg-indigo-100 text-indigo-800",
    order: 0,
  },
  scope: {
    label: "Scope agent subchat",
    short: "Scope agent",
    border: "border-l-teal-500",
    header: "bg-teal-50 border-teal-100",
    pill: "bg-teal-100 text-teal-800",
    order: 1,
  },
  subagent: {
    label: "Subagent subchat",
    short: "Subagent",
    border: "border-l-purple-500",
    header: "bg-purple-50 border-purple-100",
    pill: "bg-purple-100 text-purple-800",
    order: 2,
  },
  worker: {
    label: "Worker subchat",
    short: "Worker",
    border: "border-l-blue-500",
    header: "bg-blue-50 border-blue-100",
    pill: "bg-blue-100 text-blue-800",
    order: 3,
  },
  unknown: {
    label: "Run subchat",
    short: "Run",
    border: "border-l-gray-400",
    header: "bg-gray-50 border-gray-200",
    pill: "bg-gray-100 text-gray-700",
    order: 4,
  },
};

function classifyAgentKind(agentId, { isOrchestratorRun, isWorkerRun, isScopeAgent } = {}) {
  if (isOrchestratorRun) return "orchestrator";
  if (isScopeAgent || agentId === "scope") return "scope";
  if (isWorkerRun) return "worker";
  if (!agentId) return "unknown";
  if (agentId === "orchestrator") return "orchestrator";
  if (agentId.endsWith("_executor") || agentId === "worker" || agentId === "worker_v2") {
    return "worker";
  }
  return "subagent";
}

function shortRunId(runId) {
  if (!runId) return "—";
  return runId.length > 16 ? `${runId.slice(0, 12)}…` : runId;
}

function parseEventMeta(ev) {
  let parsed = null;
  if (ev.data) {
    try {
      parsed = JSON.parse(ev.data);
    } catch {
      parsed = null;
    }
  }
  return {
    runId: ev.run_id || parsed?.run_id || null,
    agentId: ev.agent_id || parsed?.agent_id || null,
    parentRunId: ev.parent_run_id || parsed?.parent_run_id || null,
    parsed,
  };
}

export function buildRunRegistry(
  events,
  fanOuts,
  orchestratorRunId,
  orchestratorAgentId,
  orchestratorSkill
) {
  const workerByRunId = new Map();
  for (const fo of fanOuts || []) {
    for (const w of fo.workers || []) {
      if (w.run_id) workerByRunId.set(w.run_id, w);
    }
  }

  const runs = new Map();

  const upsert = (runId, patch) => {
    if (!runId) return;
    const prev = runs.get(runId) || { runId, eventCount: 0 };
    runs.set(runId, { ...prev, ...patch, runId });
  };

  if (orchestratorRunId) {
    upsert(orchestratorRunId, {
      kind: "orchestrator",
      agentId: orchestratorAgentId,
      skill: orchestratorSkill,
      parentRunId: null,
    });
  }

  for (const [runId, w] of workerByRunId) {
    upsert(runId, {
      kind: "worker",
      agentId: w.skill || w.run_id,
      skill: w.skill,
      url: w.url,
      parentRunId: orchestratorRunId || null,
      outcomeClass: w.outcome_class,
      status: w.status,
    });
  }

  for (const ev of events) {
    const meta = parseEventMeta(ev);
    const runId = meta.runId || orchestratorRunId;
    if (!runId) continue;

    const worker = workerByRunId.get(runId);
    const isOrchestratorRun = runId === orchestratorRunId;
    const kind = classifyAgentKind(meta.agentId, {
      isOrchestratorRun,
      isWorkerRun: !!worker,
      isScopeAgent: meta.agentId === "scope",
    });

    upsert(runId, {
      agentId: meta.agentId || undefined,
      parentRunId: meta.parentRunId || (worker ? orchestratorRunId : undefined),
      kind: runs.get(runId)?.kind || kind,
      firstTs: Math.min(runs.get(runId)?.firstTs ?? ev.timestamp, ev.timestamp),
      lastTs: Math.max(runs.get(runId)?.lastTs ?? ev.timestamp, ev.timestamp),
    });
    const cur = runs.get(runId);
    if (cur) cur.eventCount = (cur.eventCount || 0) + 1;

    if (ev.type === "subagent_spawn") {
      const f = getEventFields(ev);
      if (f.child_run_id) {
        upsert(f.child_run_id, {
          kind: classifyAgentKind(f.child_agent_id, {
            isScopeAgent: f.child_agent_id === "scope",
          }),
          agentId: f.child_agent_id,
          parentRunId: runId,
          spawnTask: f.task,
          firstTs: ev.timestamp,
        });
      }
    }

    if (ev.type === "agent_start") {
      upsert(runId, {
        agentId: meta.agentId || undefined,
        parentRunId: meta.parentRunId || undefined,
      });
    }
  }

  for (const [runId, info] of runs) {
    const worker = workerByRunId.get(runId);
    if (worker && !info.url) {
      info.url = worker.url;
      info.skill = worker.skill;
      info.outcomeClass = worker.outcome_class;
    }
    if (!info.kind) {
      info.kind = classifyAgentKind(info.agentId, {
        isOrchestratorRun: runId === orchestratorRunId,
        isWorkerRun: !!worker,
        isScopeAgent: info.agentId === "scope",
      });
    }
  }

  return { runs, workerByRunId };
}

function runSectionTitle(info, runId) {
  const cfg = RUN_KIND[info.kind] || RUN_KIND.unknown;
  if (info.kind === "orchestrator") {
    return cfg.label;
  }
  if (info.kind === "scope") {
    return cfg.label;
  }
  if (info.kind === "worker") {
    return `${cfg.label}: ${info.skill || info.agentId || shortRunId(runId)}`;
  }
  if (info.kind === "subagent") {
    return `${cfg.label}: ${info.agentId || shortRunId(runId)}`;
  }
  return `${cfg.label} · ${info.agentId || shortRunId(runId)}`;
}

function sortRunSections(sections, orchestratorRunId) {
  return [...sections].sort((a, b) => {
    const ka = RUN_KIND[a.info.kind]?.order ?? 99;
    const kb = RUN_KIND[b.info.kind]?.order ?? 99;
    if (ka !== kb) return ka - kb;
    if (a.runId === orchestratorRunId) return -1;
    if (b.runId === orchestratorRunId) return 1;
    const ta = a.info.firstTs ?? 0;
    const tb = b.info.firstTs ?? 0;
    if (ta !== tb) return ta - tb;
    return a.runId.localeCompare(b.runId);
  });
}

function groupEventsByRunId(events, orchestratorRunId) {
  const byRun = new Map();
  for (const ev of events) {
    const meta = parseEventMeta(ev);
    const rid = meta.runId || orchestratorRunId || "__unknown__";
    if (!byRun.has(rid)) byRun.set(rid, []);
    byRun.get(rid).push(ev);
  }
  return byRun;
}

function TurnDivider({ turnIndex, type }) {
  const isStart = type === "turn_start";
  if (!isStart) return null;
  return (
    <div className='relative pl-8 my-4 first:mt-0'>
      <div className='absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-gray-200 border-4 border-white shadow-sm' />
      <div className='flex items-center gap-2'>
        <span className='text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full border border-gray-200/60'>
          Turn {turnIndex ?? "?"}
        </span>
        <div className='flex-1 h-px bg-gray-100' />
      </div>
    </div>
  );
}

function RunSubchatSection({
  runId,
  info,
  events,
  registry,
  defaultOpen,
  nested,
  showSystemEvents = false,
  childSections = [],
  renderChildSection = null,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const kindCfg = RUN_KIND[info.kind] || RUN_KIND.unknown;
  const title = runSectionTitle(info, runId);
  const errorCount = events.filter((e) => e.is_error).length;
  const shotCount = events.filter(
    (e) => e.type === "tool_call" && isScreenshotTool(getEventFields(e).tool_name)
  ).length;
  const parentInfo = info.parentRunId ? registry.get(info.parentRunId) : null;
  const parentLabel = parentInfo
    ? runSectionTitle(parentInfo, info.parentRunId)
    : info.parentRunId
      ? shortRunId(info.parentRunId)
      : null;
  const tsStart = info.firstTs ? new Date(info.firstTs).toLocaleTimeString() : null;
  const tsEnd = info.lastTs ? new Date(info.lastTs).toLocaleTimeString() : null;
  const taskPreview = spawnTaskPreview(info.spawnTask);

  const preparedEvents = useMemo(() => {
    return prepareCleanEvents(events, showSystemEvents);
  }, [events, showSystemEvents]);

  const timelineItems = useMemo(
    () => buildInlineTimelineItems(preparedEvents, childSections),
    [preparedEvents, childSections]
  );

  const finalContext = useMemo(() => {
    const final = findFinalLlmCall(events, getEventFields);
    if (!final) return null;

    const toolResultsByCallId = new Map();
    for (const ev of events) {
      if (ev.type !== "message") continue;
      const fields = getEventFields(ev);
      if (fields.role === "tool_result" && fields.tool_call_id) {
        toolResultsByCallId.set(fields.tool_call_id, fields);
      }
    }

    const preceding = events.slice(0, final.index).map((ev) => {
      const fields = getEventFields(ev);
      const enriched = { ...ev, _fields: fields };
      if (ev.type === "tool_call" && fields.tool_call_id) {
        enriched.pairedResult = toolResultsByCallId.get(fields.tool_call_id) || null;
      }
      return enriched;
    });

    return {
      llmMeta: final.fields,
      usage: final.fields.usage,
      events: preceding,
      eventIndex: preceding.length,
    };
  }, [events]);

  return (
    <div
      className={`rounded-xl border mb-4 last:mb-0 ${
        open ? "overflow-visible" : "overflow-hidden"
      } ${kindCfg.header} ${nested ? "ml-2 sm:ml-4" : ""}`}>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className={`w-full px-4 py-3 flex flex-col gap-1.5 text-left border-l-4 ${kindCfg.border} hover:brightness-[0.98] transition-all`}>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <div className='flex flex-wrap items-center gap-2 min-w-0'>
            <span className='text-gray-500 text-sm leading-none'>{open ? "▾" : "▸"}</span>
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${kindCfg.pill}`}>
              {kindCfg.short}
            </span>
            <span className='text-sm font-semibold text-gray-900'>{title}</span>
            {info.outcomeClass && <OutcomeBadge cls={info.outcomeClass} />}
            {errorCount > 0 && (
              <span className='text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium'>
                {errorCount} error{errorCount !== 1 ? "s" : ""}
              </span>
            )}
            {childSections.length > 0 && (
              <span className='text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium border border-indigo-100'>
                {childSections.length} nested
              </span>
            )}
          </div>
          <div className='flex items-center gap-3 text-[11px] text-gray-500 shrink-0'>
            {finalContext && (
              <ContextMeter
                portal
                title="Final context"
                llmMeta={finalContext.llmMeta}
                usage={finalContext.usage}
                events={finalContext.events}
                eventIndex={finalContext.eventIndex}
              />
            )}
            {shotCount > 0 && <span>{shotCount} screenshot{shotCount !== 1 ? "s" : ""}</span>}
            <span>{preparedEvents.length} events</span>
          </div>
        </div>
        <div className='flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 pl-6'>
          <span>
            <span className='text-gray-400'>run </span>
            <span className='font-mono' title={runId}>{shortRunId(runId)}</span>
          </span>
          {info.agentId && (
            <span>
              <span className='text-gray-400'>agent </span>
              <span className='font-mono'>{info.agentId}</span>
            </span>
          )}
          {parentLabel && (
            <span>
              <span className='text-gray-400'>spawned from </span>
              <span className='font-mono' title={info.parentRunId}>{parentLabel}</span>
            </span>
          )}
          {tsStart && (
            <span>
              <span className='text-gray-400'>time </span>
              {tsStart}
              {tsEnd && tsEnd !== tsStart ? ` → ${tsEnd}` : ""}
            </span>
          )}
        </div>
        {info.url && (
          <div className='text-[11px] text-gray-600 pl-6 truncate' title={info.url}>
            <span className='text-gray-400'>page </span>
            {info.url}
          </div>
        )}
        {taskPreview && (
          <div className='text-[11px] text-gray-600 pl-6 line-clamp-2' title={typeof info.spawnTask === "string" ? info.spawnTask : taskPreview}>
            <span className='text-gray-400'>task </span>
            {taskPreview}
          </div>
        )}
      </button>
      {open && (
        <div className='bg-white border-t px-3 sm:px-5 py-5'>
          {info.spawnTask != null && info.spawnTask !== "" && (
            <div className="mb-5 w-full max-w-none">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                Spawn task
              </div>
              <TimelineMarkdown text={info.spawnTask} label="Task" />
            </div>
          )}
          <div className='relative border-l-2 border-gray-100 ml-1 sm:ml-2 space-y-5 pb-2'>
            {timelineItems.map((item, i) => {
              if (item.kind === "child") {
                return (
                  <div key={`${runId}-child-${item.section.runId}`} className="pl-2 sm:pl-4 -ml-2">
                    {renderChildSection
                      ? renderChildSection(item.section)
                      : null}
                  </div>
                );
              }

              const ev = item.ev;
              const type = ev.type || "event";
              const fields = ev._fields || getEventFields(ev);
              if (type === "turn_start" || type === "turn_end") {
                return (
                  <TurnDivider
                    key={`${runId}-turn-${i}`}
                    turnIndex={fields.turn_index}
                    type={type}
                  />
                );
              }
              return (
                <CleanEventRow
                  key={`${runId}-${ev.timestamp}-${ev.type}-${i}`}
                  ev={ev}
                  nested
                  events={preparedEvents}
                  index={preparedEvents.indexOf(ev)}
                />
              );
            })}
            {!timelineItems.length && (
              <div className="text-xs text-gray-400 italic pl-2">No events in this subchat.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function isTopLevelSection(section) {
  const { info } = section;
  // Only roots stay top-level. Workers + subagents with a parent are nested
  // under that parent at the spawn / chronological position.
  if (info.kind === "orchestrator") return true;
  if (!info.parentRunId) return true;
  return false;
}

/**
 * Build a parent timeline that inlines child subchats:
 * - after matching subagent_spawn events
 * - otherwise chronologically by the child's firstTs (fan-out workers)
 */
export function buildInlineTimelineItems(parentEvents, childSections = []) {
  const children = [...(childSections || [])];
  const used = new Set();
  const items = [];

  for (const ev of parentEvents || []) {
    items.push({ kind: "event", ev, ts: ev.timestamp ?? 0 });
    if (ev.type === "subagent_spawn") {
      const fields = ev._fields || getEventFields(ev);
      const childId = fields.child_run_id;
      if (!childId || used.has(childId)) continue;
      const child = children.find((c) => c.runId === childId);
      if (child) {
        items.push({ kind: "child", section: child, ts: ev.timestamp ?? 0 });
        used.add(childId);
      }
    }
  }

  const remaining = children
    .filter((c) => !used.has(c.runId))
    .sort((a, b) => (a.info?.firstTs ?? 0) - (b.info?.firstTs ?? 0));

  for (const child of remaining) {
    const childTs = child.info?.firstTs ?? 0;
    let insertAt = items.length;
    for (let i = 0; i < items.length; i += 1) {
      if ((items[i].ts ?? 0) > childTs) {
        insertAt = i;
        break;
      }
    }
    items.splice(insertAt, 0, { kind: "child", section: child, ts: childTs });
  }

  return items;
}

function spawnTaskPreview(task) {
  if (task == null || task === "") return null;
  if (typeof task === "object") {
    try {
      const keys = Object.keys(task);
      if (task.context) return trimText(String(task.context), 140);
      if (keys.length) return `{${keys.length} fields: ${keys.slice(0, 4).join(", ")}${keys.length > 4 ? "…" : ""}}`;
      return "{}";
    } catch {
      return null;
    }
  }
  const text = String(task).trim();
  if (
    (text.startsWith("{") && text.endsWith("}")) ||
    (text.startsWith("[") && text.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && parsed.context) {
        return trimText(String(parsed.context), 140);
      }
    } catch {
      /* fall through */
    }
  }
  return trimText(text, 140);
}

function organizeRunSections(events, fanOuts, orchestratorRunId, orchestratorAgentId, orchestratorSkill) {
  const { runs } = buildRunRegistry(
    events,
    fanOuts,
    orchestratorRunId,
    orchestratorAgentId,
    orchestratorSkill
  );
  const byRun = groupEventsByRunId(events, orchestratorRunId);

  const sections = [];
  for (const [runId, evs] of byRun) {
    if (!evs.length) continue;
    const info = runs.get(runId) || {
      runId,
      kind: "unknown",
      agentId: evs.find((e) => e.agent_id)?.agent_id || null,
      firstTs: evs[0]?.timestamp,
      lastTs: evs[evs.length - 1]?.timestamp,
      eventCount: evs.length,
    };
    sections.push({ runId, info, events: evs });
  }

  // Also surface registry-only children (spawned runs with no events yet / workers
  // known from fan-out metadata) so nesting still has a handle.
  for (const [runId, info] of runs) {
    if (byRun.has(runId)) continue;
    if (!info.parentRunId && info.kind !== "orchestrator") continue;
    sections.push({ runId, info, events: [] });
  }

  return {
    registry: runs,
    sections: sortRunSections(sections, orchestratorRunId),
  };
}

export function EventsTimelineCleanView({
  events,
  fanOuts,
  orchestratorRunId,
  orchestratorAgentId,
  orchestratorSkill,
  showSystemEvents = false,
}) {
  const { registry, sections } = useMemo(
    () =>
      organizeRunSections(
        events,
        fanOuts,
        orchestratorRunId,
        orchestratorAgentId,
        orchestratorSkill
      ),
    [events, fanOuts, orchestratorRunId, orchestratorAgentId, orchestratorSkill]
  );

  const topLevel = sections.filter((s) => isTopLevelSection(s));
  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const s of sections) {
      if (isTopLevelSection(s)) continue;
      const parentId = s.info.parentRunId;
      if (!parentId) continue;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId).push(s);
    }
    for (const kids of map.values()) {
      kids.sort((a, b) => (a.info.firstTs ?? 0) - (b.info.firstTs ?? 0));
    }
    return map;
  }, [sections]);

  if (!events.length) return null;

  const renderSection = (section, { nested = false } = {}) => {
    const kids = childrenByParent.get(section.runId) || [];
    const defaultOpen =
      section.info.kind === "orchestrator" ||
      section.info.kind === "worker" ||
      nested ||
      section.events.some((e) => e.is_error) ||
      sections.length <= 4;

    return (
      <RunSubchatSection
        key={section.runId}
        runId={section.runId}
        info={section.info}
        events={section.events}
        registry={registry}
        defaultOpen={defaultOpen}
        nested={nested}
        showSystemEvents={showSystemEvents}
        childSections={kids}
        renderChildSection={(child) => renderSection(child, { nested: true })}
      />
    );
  };

  return (
    <div className='p-2 sm:p-3 space-y-1'>
      <div className='px-1 pb-2 text-[11px] text-gray-500'>
        {sections.length} subchat{sections.length !== 1 ? "s" : ""} · subagents and fan-out workers
        nest under their parent at the spawn / start time
      </div>
      {topLevel.map((section) => renderSection(section))}
    </div>
  );
}

export function EventsTimelineRawView({ events }) {
  return (
    <>
      {events.map((ev, i) => (
        <div key={i} className='px-4 py-2 flex items-start gap-3'>
          <span className='text-[11px] text-gray-400 whitespace-nowrap pt-0.5'>
            {new Date(ev.timestamp).toLocaleTimeString()}
          </span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
              ev.is_error ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"
            }`}>
            {ev.type || "event"}
          </span>
          {ev.agent_id && (
            <span className='text-[10px] text-gray-400 font-mono'>{ev.agent_id}</span>
          )}
          <pre className='text-xs text-gray-700 whitespace-pre-wrap break-all flex-1'>
            {ev.data}
          </pre>
        </div>
      ))}
    </>
  );
}
